import { computed, onBeforeUnmount } from "vue";
import { useIntersectionObserver } from "@vueuse/core";
import { logEvent } from "~/utils/firebase";
import { useI18n } from "~/composables/useI18n";

const PAGE_NAME = "about_us";

export const ABOUT_SECTION_ID = {
	HERO: "about_hero",
	BRAND_CONCEPT: "brand_concept",
	NEWS: "news",
	BUSINESS: "business",
	INSTITUTION_ENTRY: "institution_entry",
	PARTNER_MEDIA: "partner_media",
	APP_DOWNLOAD: "about_app_download",
	FOOTER: "footer",
};

export const BUSINESS_PARTNER_META = [
	{ partner_id: "hk_group", region: "hk" },
	{ partner_id: "tw_agent", region: "tw" },
	{ partner_id: "my_branch", region: "my" },
];

export const CONTACT_TYPE_MAP = {
	location: "address",
	phone: "phone",
	mobile: "phone",
	number: "phone",
	email: "email",
	whatsapp: "whatsapp",
};

function getEntrySource() {
	if (!import.meta.client) return "direct";
	const referrer = document.referrer;
	if (!referrer) return "direct";

	try {
		const referrerUrl = new URL(referrer);
		if (referrerUrl.host === window.location.host) return "internal";
		return referrerUrl.host;
	} catch {
		return "unknown";
	}
}

function getLoginStatus() {
	if (!import.meta.client) return "guest";
	try {
		const hasToken = !!localStorage.getItem("token");
		if (hasToken) return "logged_in";
		const gwUser = localStorage.getItem("gwUser");
		return gwUser && gwUser !== "{}" ? "logged_in" : "guest";
	} catch {
		return "guest";
	}
}

function cleanParams(params) {
	return Object.fromEntries(
		Object.entries(params).filter(
			([, value]) => value !== undefined && value !== null && value !== ""
		)
	);
}

export function getNewsId(item = {}) {
	return String(item.id || item.resource_id || item.adv_id || item.link || item.title || "");
}

export function truncateTitle(title, maxLength = 100) {
	if (!title) return "";
	return title.length > maxLength ? `${title.slice(0, maxLength)}...` : title;
}

export function useAboutUsTracking(deviceType = "pc") {
	const { locale } = useI18n();
	const impressedKeys = new Set();
	const impressionTimers = new Map();

	const baseParams = computed(() =>
		cleanParams({
			login_status: getLoginStatus(),
			language: locale?.value === "en" ? "en" : "zh_hant",
			device_type: deviceType,
			page_name: PAGE_NAME,
			entry_source: getEntrySource(),
		})
	);

	function track(eventName, params = {}) {
		const mergedParams = cleanParams({ ...baseParams.value, ...params });
		logEvent(eventName, mergedParams);
	}

	function observeImpression(key, targetRef, onFire, options = {}) {
		const { threshold = 0.5, delay = 1000 } = options;

		const { stop } = useIntersectionObserver(
			targetRef,
			([entry]) => {
				if (impressedKeys.has(key)) return;

				if (entry?.isIntersecting && entry.intersectionRatio >= threshold) {
					if (impressionTimers.has(key)) return;
					const timer = window.setTimeout(() => {
						if (impressedKeys.has(key)) return;
						impressedKeys.add(key);
						onFire();
						stop();
					}, delay);
					impressionTimers.set(key, timer);
				} else if (impressionTimers.has(key)) {
					window.clearTimeout(impressionTimers.get(key));
					impressionTimers.delete(key);
				}
			},
			{ threshold: [threshold] }
		);

		onBeforeUnmount(() => {
			if (impressionTimers.has(key)) {
				window.clearTimeout(impressionTimers.get(key));
				impressionTimers.delete(key);
			}
			stop();
		});

		return stop;
	}

	return {
		track,
		observeImpression,
		baseParams,
	};
}
